import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { black } from '@avangard/ui/colors';

import { useEnqueueStacks } from '@modules/layout/hooks';
import { DeskCard } from '@modules/layout/moleculas';
import { UpdateLapAccessMutation } from '@modules/lap/graphql';
import { GetStudentQuery } from '@modules/students/graphql';
import { StudentLapAccessActions } from './student-lap-access-actions';

import type { LapAccessEntity } from '@modules/lap/entities';
import type { StudentEntity } from '@modules/students/entities';
import type {
    UpdateLapAccessMutationType,
    UpdateLapAccessMutationVariables,
} from '@modules/types/graphql';

type StudentLapAccessCardProps = {
    lapAccess: LapAccessEntity;
    student: StudentEntity;
    disableActions: boolean;
    disableBlocking?: boolean;
};

const CardDetails = styled.div`
    width: 100%;

    > p {
        width: 100%;
        color: ${black[50]};
        font-size: 1.4rem;

        &:not(:last-child) {
            margin-bottom: 24px;
        }
    }
`;

const StudentLapAccessCard = (props: StudentLapAccessCardProps): React.ReactElement => {
    const { lapAccess, student, disableBlocking, disableActions } = props;

    const { t } = useTranslation(['students']);

    const { enqueueSuccess } = useEnqueueStacks();

    const [updateLapAccess] = useMutation<
        UpdateLapAccessMutationType,
        UpdateLapAccessMutationVariables
    >(UpdateLapAccessMutation);

    const handleUpdateLapAccess = async (): Promise<void> => {
        try {
            const { data: updateLapAccessData } = await updateLapAccess({
                variables: {
                    id: lapAccess.id,
                    item: {
                        lapId: lapAccess.lap.id,
                        blocked: !lapAccess.blocked,
                    },
                },
                awaitRefetchQueries: true,
                refetchQueries: [{ query: GetStudentQuery, variables: { id: student.id } }],
            });

            const result = updateLapAccessData?.updateLapAccess;

            if (!result) {
                throw new Error(t('students:notifiers.edit_access.error') ?? '');
            }

            enqueueSuccess(t('students:notifiers.edit_access.success'));
        } catch (e) {
            console.log(e);
            throw e;
        }
    };

    const lap = lapAccess.lap;

    return (
        <>
            <DeskCard
                partialDisabled={lapAccess.blocked}
                title={lap.getPlatformTitle() ?? '-'}
                ActionsComponent={
                    !disableActions ? (
                        <StudentLapAccessActions
                            blocked={lapAccess.blocked}
                            onSetAccess={handleUpdateLapAccess}
                            disableBlocking={disableBlocking}
                        />
                    ) : null
                }
            >
                <CardDetails>
                    <p>{lap.course.name || '-'}</p>
                    <p>{lap.getFullDate()}</p>
                </CardDetails>
            </DeskCard>
        </>
    );
};

export { StudentLapAccessCard };
